import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { createSearchFilterClickEvent } from '@/scopes/search/search/domain/analytics'
import { useAiFilter } from '@/scopes/search/search/domain/hooks'
import { isFlightOnlySelector } from '@/scopes/search/search/infrastructure/store/selectors'
import { useSearchStoreSelector } from '@/scopes/search/search/infrastructure/store/useSearchStore'
import { space } from '@sunwebgroup/ui-components'
import { Accordion, Button, TextArea } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

const MAX_LENGTH = 250

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
`

export function IntelligentFilter() {
  const intl = useIntl()
  const { setUserQuery, isFetching, isAIError, userQuery } = useAiFilter()
  const { sendEvent } = useAnalytics()
  const [value, setValue] = React.useState(userQuery ?? '')
  const isFlightOnly = useSearchStoreSelector(isFlightOnlySelector)
  const showAIFilter = useFeatureFlag('ENABLE_AI_FILTER') && !isFlightOnly

  React.useEffect(() => {
    setValue(userQuery ?? '')
  }, [userQuery])

  const confirm = () => {
    setUserQuery(value)
    sendEvent(
      createSearchFilterClickEvent({
        element: 'textField',
        type: 'AI question',
        result: value,
      }),
    )
  }

  const changeHandler = ({ target: { value: newValue } }: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (newValue.length <= MAX_LENGTH) {
      setValue(newValue)
    }
  }

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    confirm()
  }

  const keyPressedHandler = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      confirm()
    }
  }

  return (
    showAIFilter && (
      <Accordion.Item title={intl.formatMessage({ id: 'search_ai_filter_label' })} defaultOpen>
        <Form onSubmit={submitHandler}>
          <TextArea
            value={value}
            onChange={changeHandler}
            placeholder={intl.formatMessage({ id: 'search_ai_filter_placeholder' })}
            onKeyDown={keyPressedHandler}
            disabled={isFetching}
            status={isAIError ? 'error' : 'default'}
            label={intl.formatMessage({ id: 'search_ai_filter_hint' })}
            feedbackText={intl.formatMessage({ id: 'search_ai_filter_error' })}
          />
          <Button type="submit" disabled={isFetching} $type="secondary" $size="S" $fullWidth>
            {intl.formatMessage({ id: 'search_ai_filter_submit_text' })}
          </Button>
        </Form>
      </Accordion.Item>
    )
  )
}
